/* eslint-disable prettier/prettier */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { updateItemQty } from '../../../../autoshipState/autoshipSlice';
import { useDispatch } from 'react-redux';
import Xbutton from '../../Xbutton';
import { format } from '../../Utils/currencyFormatter';
import t from '@rcm/i18n-client';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';

interface Props {
  item: any;
  mobile?: boolean;
  id?: string;
}

const Item = ({ item, mobile, id }: Props) => {
  const dispatch = useDispatch();

  const [tempQty, setTempQty] = useState<number>(item.quantity ?? 1);
  const formattedPrice = format(Number(item.price * (item.quantity ?? 1)));
  const enableImage = useMediaQuery('(min-width:340px)');

  useEffect(() => {
    // this enables editing on the textfield
    setTempQty(item.quantity);
  }, [item.quantity]);

  const handleQtyChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setTempQty(null);
    } else if (isNumber(value) && Number(value <= 200) && Number(value >= 0)) {
      setTempQty(value);
      if (mobile || e.clickChange) {
        handleQtyBlur(e);
      }
    }
  };

  const isNumber = (value?: string | number): boolean => {
    return value != null && value !== '' && !isNaN(Number(value.toString()));
  };

  const handleQtyBlur = (e) => {
    /* having type='number' puts arrows on the input,
     * which makes handling the 'onBlur' and 'onChange'
     * functions harder to handle. We don't want to make
     * requests in the onChange.
     */
    const oldValue = String(e.target.value);

    const newValue = Number(oldValue.replace(/\D/g, ''));
    // if the value hasn't changed or is otherwise not valid, dont make the request
    if (
      newValue === item.quantity ||
      newValue > 200 ||
      (mobile && (!isNumber(oldValue) || oldValue.length < 1))
    ) {
      return;
    }

    dispatch(updateItemQty({ id: item.itemCode, quantity: newValue }));
  };

  const formattedName = (name) => {
    if (name.length < 32) return name;
    return name.substring(0, 28) + '...';
  };

  const handleDeleteItem = () => {
    dispatch(updateItemQty({ id: item.itemCode, quantity: 0 }));
    return;
  };

  const calculatedBv = item.bv * (item.quantity ?? 1);
  const calculatedPv = item.pv * (item.quantity ?? 1);

  const quantityComponent = (
    <div
      css={{
        maxWidth: '100px',
        display: 'flex',
        flexDirection: 'row',
        alignSelf: mobile ? 'end' : 'baseline',
        alignItems: 'baseline',
      }}
    >
      <div
        id='dec'
        css={{ cursor: 'pointer' }}
        onClick={() =>
          handleQtyChange({ target: { value: tempQty - 1 }, clickChange: true })
        }
      >
        {item.quantity === 1 ? '🗑️' : '➖'}
      </div>
      <input
        id={`qty-select-${item.itemCode}`}
        name={id}
        onBlur={handleQtyBlur}
        onChange={handleQtyChange}
        value={tempQty}
        css={{
          width: '45px',
          height: mobile ? '36px' : '22px',
          background: 'white',
          borderRadius: '4px',
          border: '1px solid #D8D8D8',
          fontSize: '16px',
          textAlign: 'center',
          paddingTop: '8px',
          margin: '0px 8px 0px 8px',
        }}
      />
      <div
        id='inc'
        css={{ cursor: 'pointer' }}
        onClick={() =>
          handleQtyChange({ target: { value: tempQty + 1 }, clickChange: true })
        }
      >
        ➕
      </div>
    </div>
  );

  if (mobile) {
    return (
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          fontSize: '16px',
        }}
      >
        {enableImage ? (
          <div css={{ textAlign: 'center', width: '75px' }}>
            <img
              src={item.imgUrl}
              css={{
                maxWidth: '48px',
                maxHeight: '75px',
                alignSelf: 'flex-start',
                marginRight: '6px',
              }}
            />
          </div>
        ) : null}

        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '-webkit-fill-available',
            justifyContent: 'flex-start',
          }}
        >
          <div css={{ display: 'flex', justifyContent: 'space-between' }}>
            <a href={`/p/${item.detailPage}`}>{formattedName(item.name)}</a>
            <div css={{ marginRight: '-16px', minWidth: '60px' }}>
              <strong>{formattedPrice}</strong>
            </div>
          </div>
          <div
            css={{
              alignItems: 'self-end',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
            }}
          >
            <div css={{ width: '80px' }}>
              <div>BV: {calculatedBv}</div>
              <div>PV: {calculatedPv}</div>
            </div>
            {quantityComponent}
          </div>
        </div>
        <Xbutton
          sx={{
            width: '22px',
            height: '22px',
            alignSelf: 'center',
            marginLeft: '8px',
          }}
          onClick={handleDeleteItem}
        />
        <hr />
      </div>
    );
  }

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        columnGap: '8px',
        marginTop: '20px',
        marginBottom: '40px',
      }}
    >
      <div>
        <Xbutton
          id={id}
          sx={{ width: '22px', height: '22px' }}
          onClick={handleDeleteItem}
        />
      </div>  

      <div
        css={{
          minWidth: '90px',
          textAlign: 'center',
          alignSelf: 'center',
        }}
      >
        <img
          src={item.imgUrl}
          css={{
            zoom: '75%',
            maxHeight: '150px',
            padding: '8px',
          }}
        />
      </div>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          lineHeight: '20px',
          rowGap: '4px',
        }}
      >
        <Typography
          sx={(theme) => ({
            color: theme.palette.tertiary.main,
            fontSize: '18px',
            cursor: 'default',
          })}
        >
          {formattedName(item.name)}
        </Typography>
        <div>
          {t('Item code:')} {item.itemCode}
        </div>
        <strong>{formattedPrice}</strong>
        <div>BV: {item.bv * (item.quantity ?? 1)}</div>
        <div>PV: {item.pv * (item.quantity ?? 1)}</div>
        {quantityComponent}
      </div>
    </div>
  );
};

export default Item;
